import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Card from "@mui/material/Card";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";
import DataTable from "examples/Tables/DataTable";
import WTPChart from "examples/Charts/WTPChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import MultiAxisChart from "examples/Charts/MultiAxisChart";
import Footer from "examples/Footer";

import { serverBaseURL } from "common/settings";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

function EnvironmentalValue() {
  const location = useLocation();
  console.log(location);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedSiteBody, setSelectedSiteBody] = useState({});
  const [impactAndCumulativePowerGraphData, setImpactAndCumulativePowerGraphData] = useState({});
  const [impactGraphData, setImpactGraphData] = useState({});
  const [lcoeData, setLcoeData] = useState({});
  const [remainingLifetime, setRemainingLifetime] = useState(0);
  const [remainingPower, setRemainingPower] = useState(0);
  const [currentWTP, setCurrentWTP] = useState(0);
  const [tableData, setTableData] = useState({
    columns: [
      { Header: "Years", accessor: "years" },
      { Header: "Cumulative Power (kWh)", accessor: "cumulativePower" },
      {
        Header: "Environmental Impact per Functional Unit (mPts/kWh)",
        accessor: "environmentalImpact",
      },
      { Header: "Total Environmental Impact (mPts)", accessor: "total" },
    ],
    rows: [],
  });
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);
  const { getToken } = useKindeAuth();

  async function fetchSiteData(siteId) {
    const accessToken = await getToken();
    const response = await (
      await fetch(`${serverBaseURL}get_trend_result?system_type=ongrid`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    const displaySite = response.sites.filter((site) => site.site == siteId)[0];
    setRemainingLifetime(displaySite.lifetime_rates.length);
  }
  async function fetchSiteImpact(siteId) {
    const accessToken = await getToken();
    const response = await (
      await fetch(`${serverBaseURL}environmentalvalues/?site_id=${siteId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    console.log(response);
    const lifetimeYears = response.lifetime_years;
    setImpactAndCumulativePowerGraphData({
      labels: ["10", "15", "30", lifetimeYears.toString()],
      units: { x: "Years", y: "mPt/kWh", y2: "kWh" },
      datasets1: [
        {
          label: "Environmental Impact per Functional Unit (mPt/kWh)",
          data: response.environmental_impacts,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
        {
          label: "Junction box repair",
          data: response.junctionbox_repair,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
        {
          label: "Transport 100km",
          data: response.transport_100km,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
        },
        {
          label: "Transport 10000km",
          data: response.transport_10000km,
          backgroundColor: "rgba(255, 255, 0, 0.8)",
        },
      ],
      datasets2: [
        {
          label: "Cumulative power",
          data: response.cumulative_power,
          backgroundColor: "rgba(75, 255, 255, 0.8)",
        },
      ],
    });
    setImpactGraphData({
      labels: ["10", "15", "30"],
      units: { x: "Years", y: "mPt/kWh" },
      datasets: [
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 1.4%/year, No reduction in EI production and recycle)",
          data: response.environmental_impacts,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          stack: "Stack 0",
        },
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 1.4%/year, EI production and recycle reduces by 10% every 5 years)",
          data: response.environmental_impacts_2,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
          stack: "Stack 1",
        },
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 2.8%/year, No reduction in EI production and recycle)",
          data: response.environmental_impacts_3,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
          stack: "Stack 2",
        },
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 2.8%/year, EI production and recycle reduces by 10% every 5 years)",
          data: response.environmental_impacts_2,
          backgroundColor: "rgba(255, 255, 0, 0.8)",
          stack: "Stack 3",
        },
      ],
    });
    setRemainingPower(response.site_a_wtp[0]);
    setCurrentWTP(response.site_a_wtp[1]);
    setLcoeData({
      labels: [
        "40%",
        "45%",
        "50%",
        "55%",
        "60%",
        "65%",
        "70%",
        "75%",
        "80%",
        "85%",
        "90%",
        "95%",
        "100%",
      ],
      datasets: [
        {
          label: "30 years remaining lifetime",
          data: response.lcoe[0],
          backgroundColor: "rgba(255, 255, 0, 0.8)",
        },
        {
          label: "20 years remaining lifetime",
          data: response.lcoe[1],
          backgroundColor: "rgba(255, 0, 255, 0.8)",
        },
        {
          label: "15 years remaining lifetime",
          data: response.lcoe[2],
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
        {
          label: "10 years remaining lifetime",
          data: response.lcoe[3],
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
      ],
      /*
      datasets2: [
        {
          label: "site WTP",
          data: [
            {
              x: response.site_a_wtp[0],
              y: response.site_a_wtp[1],
            },
          ],
        },
      ],
      */
    });
    setTableData({
      columns: [
        { Header: "Years", accessor: "years" },
        { Header: "Cumulative Power (kWh)", accessor: "cumulativePower" },
        {
          Header: "Environmental Impact per Functional Unit (mPts/kWh)",
          accessor: "environmentalImpact",
        },
        { Header: "Total Environmental Impact (mPts)", accessor: "total" },
      ],
      rows: [
        {
          years: 10,
          cumulativePower: response.cumulative_power[0].toFixed(3),
          environmentalImpact: response.environmental_impacts[0].toFixed(3),
          total: (response.cumulative_power[0] * response.environmental_impacts[0]).toFixed(3),
        },
        {
          years: 15,
          cumulativePower: response.cumulative_power[1].toFixed(3),
          environmentalImpact: response.environmental_impacts[1].toFixed(3),
          total: (response.cumulative_power[1] * response.environmental_impacts[1]).toFixed(3),
        },
        {
          years: 30,
          cumulativePower: response.cumulative_power[2].toFixed(3),
          environmentalImpact: response.environmental_impacts[2].toFixed(3),
          total: (response.cumulative_power[2] * response.environmental_impacts[2]).toFixed(3),
        },
        {
          years: lifetimeYears,
          cumulativePower: response.cumulative_power[3].toFixed(3),
          environmentalImpact: response.environmental_impacts[3].toFixed(3),
          total: (response.cumulative_power[3] * response.environmental_impacts[3]).toFixed(3),
        },
      ],
    });
  }

  async function doSiteChanged(event) {
    handleLoadingOpen();
    sites.forEach(async (site) => {
      if (site.id === event.target.value) {
        setSelectedSiteBody(site);
      }
    });
    await fetchSiteImpact(event.target.value);
    await fetchSiteData(event.target.value);
    console.log(lcoeData);
    handleLoadingClose();
  }

  function selectChange(event) {
    setSelectedSite(event.target.value);
    doSiteChanged(event);
  }

  async function fetchSites() {
    const accessToken = await getToken();
    const response = await (
      await fetch(`${serverBaseURL}sites`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    setSites(response);
    if (location.state && location.state.prop) {
      setSelectedSite(location.state.prop.site);
      fetchSiteImpact(location.state.prop.site);
      fetchSiteData(location.state.prop.site);
    }
  }

  useEffect(() => {
    fetchSites();
  }, []);

  const manufacturingPerPanelImpact = 61.1;
  const recyclingPerPanelImpact = 0.295;
  const impactDiff = 278;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loadingOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox pb={3}>
        <br />
        <MDBox p={5}>
          <FormControl fullWidth>
            <InputLabel id="sites-label">Sites</InputLabel>
            <Select
              labelId="sites-label"
              id="sites-select"
              value={selectedSite}
              label="Select Site"
              sx={{ minHeight: "50px", maxWidth: "200px" }}
              onChange={selectChange}
            >
              {sites.map((site, index) => (
                <MenuItem key={index} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%" }}>
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  assignment
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                Total Environmental Impact per Functional Unit(mPts)
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <DataTable table={tableData} canSearch />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
          <br />
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <MultiAxisChart
                  icon={{ component: "show_chart" }}
                  title="Panel service life and cumulative power output"
                  description="&nbsp;"
                  chart={impactAndCumulativePowerGraphData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <ReportsBarChart
                  icon={{ component: "show_chart" }}
                  title="Panel service life"
                  description="&nbsp;"
                  chart={impactGraphData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item lg={12}>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                Difference in environmental impact values between 15-year and 30-year operations
                (equivalent emissions from panel manufacturing)
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Grid container spacing={3} sx={{ justifyContent: "center" }}>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Manufacturing PV panel(panels)"
                  description=""
                  value={"4.55"}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Climate change
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (4.31e2 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Ozone depletion
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.82e-5 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Ionizing radiation, human health
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.64e1 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Photochemical ozone formation, human health
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.55 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Respiratory inorganics
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.62e-5 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Non-cancer human health effects
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.27e-5 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Cancer human health effects
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.53e-7 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Acidification terrestrial and freshwater
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (3.57 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Eutrophication freshwater
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.01e-2 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Eutrophication marine
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (7.93e-1 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Eutrophication terrestrial
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (8.63 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Ecotoxicity freshwater
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.36e4 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Land use
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.11e3 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Water scarcity
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.48e2 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Resource use, energy carriers
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (4.99e3 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Resource use, minerals and metals
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.7e-2 / manufacturingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item lg={12}>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                Difference in environmental impact values between 15-year and 30-year operations
                (equivalent emissions from panel recycling)
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Grid container spacing={3} sx={{ justifyContent: "center" }}>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Recycling PV panel(panels)"
                  description=""
                  value={"440.68"}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Climate change
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (3.59 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Ozone depletion
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (5.52e-7 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Ionizing radiation, human health
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.05e-1 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Photochemical ozone formation, human health
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (5.82e-3 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Respiratory inorganics
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (6.9e-8 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Non-cancer human health effects
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (2.15e-8 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Cancer human health effects
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (9.25e-10 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Acidification terrestrial and freshwater
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (7.84e-3 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Eutrophication freshwater
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (4.52e-5 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Eutrophication marine
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.68e-3 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Eutrophication terrestrial
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.86e-2 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Ecotoxicity freshwater
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (3.04e1 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Land use
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (1.13e1 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Water scarcity
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (8.08 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Resource use, energy carriers
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(
                        (4.0e1 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Resource use, minerals and metals
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {remainingLifetime}
                      {(
                        (3.08e-5 / recyclingPerPanelImpact) *
                        impactDiff *
                        (selectedSiteBody.panel_number ?? 0)
                      ).toExponential(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={4}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Remaining lifetime (Years)
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {remainingLifetime}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Remaining Power(%)
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(remainingPower * 100).toFixed(1)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    Current WTP(Є/kWh)
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {currentWTP.toFixed(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <WTPChart
                  icon={{ component: "show_chart" }}
                  title="LCOE"
                  description="The evolution of WTP for prematurely decommissioned PV, at LCOE parity 
                              with new PV and in terms of its remaining power share and lifetime. At a remaining 
                              power share of 100% and with 30 years of lifetime remaining, the WTP for prematurely 
                              decommissioned PV is equal to the WTP for new PV. "
                  chart={lcoeData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EnvironmentalValue;
